import * as Sentry from "@sentry/react";
import posthog from "posthog-js";

import {
  type AnalyticsService,
  type Properties,
  type UserProperties,
} from "@fitness-app/data-models/domain/services/AnalyticsService";

type Dependecies = {
  postHogApiKey: string;
};

export class WebAnalyticsService implements AnalyticsService {
  posthog: typeof posthog;
  isLocalhost: boolean = window.location.hostname === "localhost";
  identified: boolean | string = false;

  constructor(dependencies: Dependecies) {
    if (!this.isLocalhost) {
      posthog.init(dependencies.postHogApiKey, {
        api_host: "https://eu.posthog.com",
        autocapture: false,
        capture_pageleave: !this.isLocalhost,
        capture_pageview: !this.isLocalhost,
        capture_performance: !this.isLocalhost,
      });
    }

    this.posthog = posthog;
  }

  track(event_name: string, properties?: Properties): void {
    if (!this.isLocalhost) {
      this.posthog.capture(event_name, properties);
    }
  }

  identify(userId: string, userProperties?: UserProperties) {
    if (this.identified === userProperties?.email) {
      // already identified
      return;
    }
    if (!this.isLocalhost) {
      this.posthog.identify(userId, {
        email: userProperties?.email || null,
        name: userProperties?.name || null,
        role: userProperties?.role || null,
      });
      this.posthog.reloadFeatureFlags();
      Sentry.setUser({
        id: userId || "",
        email: userProperties?.email ? String(userProperties?.email) : undefined,
        username: userProperties?.name ? String(userProperties?.name) : undefined,
      });
      Sentry.setTag("role", userProperties?.role ? String(userProperties?.role) : "");
    }

    this.identified = userProperties?.email || false;
  }

  updateUserProperties(userId: string, userProperties: UserProperties): void {
    if (!this.isLocalhost && userProperties.email !== this.identified) {
      this.posthog.capture("update_user_properties", {
        $set: { email: userProperties.email || null },
      });
    }
  }

  reset(): void {
    this.posthog.reset();
    this.identified = false;
    Sentry.setUser(null);
  }

  screen(name: string) {
    if (!this.isLocalhost) {
      this.posthog.capture("$pageview", { $screen_name: name });
    }
  }
}
